import { Fragment, useRef, type ReactNode } from 'react'
import { Avatar } from '../ui'
import { Status, type Tone } from './Status'
import { Cross, Tick, type Stage, type StageState } from './StageTrack'
import { Toggletip } from './Toggletip'

// The vertical chain: the same stages as a track, with room for who, when and
// what is in the way.
//
// # ONE LINE PER STAGE
//
// A stage shows its role, one status, the person and a time. The reason the
// stage exists goes behind the (i), and the single thing blocking it gets a
// row of its own in critical so it is not read as one more detail. Anything
// longer belongs in a drawer the caller opens, not in this list.
//
// # MOTION
//
// A tick that was already there when the list first drew stays still. A stage
// that signs while the reader watches draws its tick, which is the one thing
// that changed.

export type ListStage = Stage & { person?: string; when?: string; tip?: string; blocker?: ReactNode; detail?: ReactNode }

const WORD: Record<StageState, { text: string; tone: Tone }> = {
  done: { text: 'Signed', tone: 'plain' },
  current: { text: 'Awaiting', tone: 'brand' },
  upcoming: { text: 'Not started', tone: 'plain' },
  rejected: { text: 'Rejected', tone: 'crit' },
  blocked: { text: 'Blocked', tone: 'crit' },
}

export function StageList({ stages }: { stages: ListStage[] }) {
  // Every stage and state present on first draw; those never animate.
  const initial = useRef<Set<string> | null>(null)
  if (initial.current === null) initial.current = new Set(stages.map((s) => s.key + '|' + s.state))
  const still = initial.current

  return (
    <ol className="kit-chain">
      {stages.map((s, i) => {
        const word = WORD[s.state]
        return (
          <li
            key={s.key}
            className={'kit-link kit-' + s.state + (still.has(s.key + '|' + s.state) ? ' kit-still' : '')}
            aria-current={s.state === 'current' || s.state === 'blocked' ? 'step' : undefined}
          >
            <span className="kit-node" aria-hidden="true">
              {s.state === 'done' ? (
                <Tick key="tick" />
              ) : s.state === 'rejected' ? (
                <Cross key="cross" />
              ) : s.state === 'blocked' ? (
                <Fragment key="bang">!</Fragment>
              ) : (
                <Fragment key="n">{i + 1}</Fragment>
              )}
            </span>
            <div className="kit-link-head">
              <span className="kit-link-role">{s.label}</span>
              <Status tone={word.tone}>{word.text}</Status>
              {s.tip && <Toggletip label={'About ' + s.label}>{s.tip}</Toggletip>}
            </div>
            {(s.when ?? s.meta) && <span className="kit-link-when">{s.when ?? s.meta}</span>}
            {(s.person || s.sub || s.detail) && (
              <div className="kit-link-detail">
                {s.person && (
                  <span className="kit-link-person">
                    <Avatar name={s.person} size={24} />
                    {s.person}
                  </span>
                )}
                {s.sub && <span>{s.sub}</span>}
                {s.detail}
              </div>
            )}
            {s.blocker && <div className="kit-link-blocker">{s.blocker}</div>}
          </li>
        )
      })}
    </ol>
  )
}
